import { useState, useEffect, useCallback, useReducer, ChangeEvent, Fragment } from 'react';
import ReactFlagsSelect from 'react-flags-select';
import { alertCreateBoxRenderArr, alertTypeObj } from '@models/manager/account/account';
import { ICreateAlert } from '@models/manager/account/accountModel';
import { gameProviderTypes } from '@configs/layout';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@reducers/index';
import { CommonUtil } from '@utils/common/common';
import { fetchAlertList } from '@reducers/account/thunks/alertThunk';
import { setAlertBoxType } from '@reducers/account/alertSlice';
import { createObjReducer } from './createAlertBoxReducer';
import { Countries2DigitCode } from '@models/commonModel';
import { getLocaleValueForFlag } from '@utils/common/language';
import _ from 'lodash';
import { axiosDummyReqTypeInstance } from '@utils/axios/axiosInstance';

const CreateAlertBox = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { myAppObj, comptitorApps } = useSelector(({ LAYOUT }: RootState) => LAYOUT);

  const [createAlertObj, dispatchCreateAlertObj] = useReducer(createObjReducer, {
    alertName: '',
    alertType: 'app',
    alertPeirod: 'daily',
    appList: [],
    country: getLocaleValueForFlag(), // 추후 등록된 reducer 에서 가져오기
    store: myAppObj.providerType,
    alertEmail: '',
    alertClass: [],
  });

  const [searchText, setSearchText] = useState('');
  const [searchResultList, setSearchResultList] = useState([]); // 검색시 라이브러리에서 가져오는 목록들

  const countriesArr = Countries2DigitCode.map((item) => item.code);
  const [searchListFlag, setSearchListFlag] = useState(false);

  // 아래 랜더링을 위한 변수
  const { alertTypeArr, alertPeirodTypeArr, alertClassArr, alertStoreTypeArr } = alertCreateBoxRenderArr();

  // searchText 검색시 디바운스처리 요청!
  useEffect(() => {
    if (searchText.trim().length > 0) {
      CommonUtil.debounceRequest({
        params: { searchText: searchText, country: createAlertObj.country },
        requestMethod: 'GET',
        url: '/api/appSearchResultList.json',
        setState: (data) => setSearchResultList(data),
      });
    }
  }, [searchText]);

  const onChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value, name } = event.currentTarget;
      switch (name) {
        case 'alertName':
          dispatchCreateAlertObj({ type: 'alertName', state: { alertNameVal: value } });
          break;
        case 'alertType':
          dispatchCreateAlertObj({ type: 'alertType', state: { alertTypeVal: value } });
          break;
        case 'alertPeriod':
          dispatchCreateAlertObj({ type: 'alertPeriod', state: { alertPeirodVal: value } });
          break;
        case 'store':
          dispatchCreateAlertObj({ type: 'store', state: { storeVal: value } });
          break;
        case 'alertEmail':
          dispatchCreateAlertObj({ type: 'alertEmail', state: { alertEmailVal: value } });
          break;
        case 'alertClass':
          dispatchCreateAlertObj({ type: 'alertClass', state: { alertClassVal: value } });
          break;
        default:
          break;
      }
    },
    [dispatchCreateAlertObj]
  );

  // 앱검색 onFocus, onBlur 처리
  const onFocusSearchTextHandler = useCallback(() => {
    // 현재 searchText 가 빈값이면 기존에 등록된 내앱 / 경쟁앱 이 리스트로 떠야한다.
    if (searchText.trim().length === 0) {
      let resultList = [];
      resultList.push(CommonUtil.objectGenerator('myApp', 'My App'));
      resultList.push(myAppObj ? myAppObj : CommonUtil.objectGenerator('myApp', ''));
      resultList.push(CommonUtil.objectGenerator('competitorApps', 'Competitor Apps'));
      if (comptitorApps?.length > 0) {
        comptitorApps.forEach((appObj) => {
          resultList.push(appObj);
        });
      } else {
        resultList.push(CommonUtil.objectGenerator('competitorApps', ''));
      }

      setSearchResultList(resultList);
    }
    setSearchListFlag(true);
  }, [searchText, myAppObj, comptitorApps, CommonUtil]);
  const onBlurSearchTextHandler = useCallback(() => {
    setSearchListFlag(false);
  }, []);

  // listenClickHandler
  const onClickAppListHandler = useCallback(
    (selectedObj) => {
      dispatchCreateAlertObj({ type: 'appList', state: { appListVal: selectedObj } });
    },
    [dispatchCreateAlertObj]
  );

  // listDel
  const onClickAppListDelHandler = useCallback(
    (selectedObj) => {
      dispatchCreateAlertObj({ type: 'delAppList', state: { appListVal: selectedObj } });
    },
    [dispatchCreateAlertObj]
  );

  // searchText
  const onChangeSearchText = useCallback((event) => {
    const { value } = event.target;
    setSearchText(value);
  }, []);

  const onSelectCountry = useCallback(
    (code) => {
      dispatchCreateAlertObj({ type: 'country', state: { countryVal: code } });
    },
    [dispatchCreateAlertObj]
  );

  const onClickCreateAlert = useCallback(async () => {
    const { alertClass, alertEmail, alertName, appList } = createAlertObj;
    if (alertName.trim().length < 5) {
      return alert('Alert names must be no more than 5 characters long.');
    } else if (alertName.trim().length >= 30) {
      return alert('Alert names must be shorter than 30 characters.');
    }
    if (appList.length === 0) {
      return alert('You must register apps to alert');
    }
    if (alertEmail.trim().length === 0) {
      return alert('You must write email to alert');
    } else if (_.isNull(alertEmail.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/))) {
      return alert('Invalid Email format, write a right your email address');
    }
    if (alertClass.length === 0) {
      return alert('Alert class must be choosen at least one');
    }

    // 생성 요청 처리 및 기존 리덕스 alertList에 추가처리
    const { data, status, statusText } = await axiosDummyReqTypeInstance<ICreateAlert>(createAlertObj).get(
      '/api/commonGetResponse.json'
    );
    if (status === 200) {
      const responseData = await dispatch(fetchAlertList()).unwrap();
      console.log('responseData: ', responseData);
      if (responseData?.length > 0) {
        dispatch(setAlertBoxType({ boxType: alertTypeObj.listBox })); // alertlist 로 화면 전환처리
      } else {
        alert('dispatchAlertList 에러 발생');
      }
    } else {
      alert(statusText);
    }
  }, [createAlertObj, dispatch, fetchAlertList, setAlertBoxType, alertTypeObj, axiosDummyReqTypeInstance]);

  const onClickMoveList = useCallback(() => {
    dispatch(setAlertBoxType({ boxType: alertTypeObj.listBox })); // alertlist 로 화면 전환처리
  }, [alertTypeObj, dispatch, setAlertBoxType]);

  return (
    <>
      <h2 className="account-tit">Create Alert</h2>
      {/* 테이블 */}
      <div className="tb-basic-wrap tb-type-1">
        <div className="tb-basic-inner">
          <table>
            <colgroup>
              <col width="18%" />
              <col width="31%" />
              <col width="20%" />
              <col width="31%" />
            </colgroup>
            <tbody>
              <tr>
                <th scope="col">Alert Name</th>
                <td colSpan={3}>
                  <label className="ipt">
                    <input
                      type="text"
                      placeholder="Please enter a name for the alert"
                      name="alertName"
                      value={createAlertObj.alertName}
                      onChange={onChangeHandler}
                    />
                  </label>
                </td>
              </tr>
              <tr>
                <th scope="col">Alert Type</th>
                <td>
                  <div className="radio-box">
                    {alertTypeArr.map((typeObj, index) => (
                      <Fragment key={index}>
                        <input
                          type="radio"
                          name="alertType"
                          className="ipt-radio"
                          id={`radio-${typeObj.value}${index}`}
                          checked={createAlertObj.alertType === typeObj.value ? true : false}
                          onChange={onChangeHandler}
                          value={typeObj.value}
                        />
                        <label htmlFor={`radio-${typeObj.value}${index}`}>
                          <span>{typeObj.text}</span>
                        </label>
                      </Fragment>
                    ))}
                  </div>
                </td>
                <th scope="col">Alert Period/Cycle</th>
                <td>
                  <div className="radio-box">
                    {alertPeirodTypeArr.map((typeObj, index) => (
                      <Fragment key={index}>
                        <input
                          type="radio"
                          name="alertPeriod"
                          className="ipt-radio"
                          id={`radio-${typeObj.value}${index}`}
                          checked={createAlertObj.alertPeirod === typeObj.value ? true : false}
                          value={typeObj.value}
                          onChange={onChangeHandler}
                        />
                        <label htmlFor={`radio-${typeObj.value}${index}`}>
                          <span>{typeObj.text}</span>
                        </label>
                      </Fragment>
                    ))}
                  </div>
                </td>
              </tr>
              <tr>
                <th scope="col">Search App</th>
                <td colSpan={3}>
                  <div className={`search-app ${searchListFlag ? 'active' : ''}`}>
                    {/* <!-- input 입력시 .active 추가 - 검색 레이어 노출 --> */}
                    <div className="search-box type-1">
                      <label>
                        <input
                          type="text"
                          className="search-ipt"
                          placeholder="Search for any app by name or id"
                          value={searchText}
                          onChange={onChangeSearchText}
                          onFocus={onFocusSearchTextHandler}
                          onBlur={onBlurSearchTextHandler}
                        />
                      </label>
                      {searchResultList?.length > 0 && (
                        <ul className={`search-list ${searchListFlag ? 'active' : ''}`}>
                          {searchResultList.map((listObj, index) => (
                            <li
                              key={listObj.appId}
                              onClick={() => onClickAppListHandler(listObj)}
                              onMouseDown={() => onClickAppListHandler(listObj)}
                            >
                              {listObj.hasOwnProperty('myApp') || listObj.hasOwnProperty('competitorApps') ? (
                                <span>
                                  <b>{listObj.value}</b>
                                </span>
                              ) : (
                                <button style={{ width: 'inherit', height: 'inherit' }} type="button">
                                  {listObj.appType === gameProviderTypes.GooglePlayStore ? (
                                    <p className="btn-ico-single android"></p>
                                  ) : (
                                    <p className="btn-ico-single app"></p>
                                  )}
                                  <img style={{ width: 'inherit', height: 'inherit' }} src={listObj.icon} />
                                  <span>{listObj.title}</span>
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <ul className="chart-ranking-data">
                      {createAlertObj.appList.map((appObj, index) => (
                        <li key={appObj.appId}>
                          <div className="game-img">
                            <img src={appObj.icon} />
                          </div>
                          <div className="game-info">
                            <p className="game-tit">{appObj.title}</p>
                            <p className="game-desc">{appObj.developer}</p>
                          </div>
                          <button
                            type="button"
                            className="btn-ico-single app-del"
                            onClick={() => onClickAppListDelHandler(appObj)}
                          >
                            <span className="blind">삭제</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </td>
              </tr>
              <tr>
                <th scope="col">Search Country</th>
                <td>
                  <div className="select-api">
                    <ReactFlagsSelect
                      selected={createAlertObj.country}
                      onSelect={onSelectCountry}
                      countries={countriesArr}
                      searchable={true}
                    />
                  </div>
                </td>
                <th scope="col">Store</th>
                <td>
                  <div className="radio-box">
                    {alertStoreTypeArr.map((typeObj, index) => (
                      <Fragment key={typeObj.value}>
                        <input
                          type="radio"
                          name="store"
                          className="ipt-radio"
                          id={`radio-${typeObj.name}${index}`}
                          value={typeObj.value}
                          checked={typeObj.value === createAlertObj.store}
                          onChange={onChangeHandler}
                        />
                        <label htmlFor={`radio-${typeObj.name}${index}`}>
                          <span>{typeObj.text}</span>
                        </label>
                      </Fragment>
                    ))}
                  </div>
                </td>
              </tr>
              <tr>
                <th scope="col">Alert reception email</th>
                <td colSpan={3}>
                  <div>
                    <label className="ipt">
                      <input
                        type="text"
                        placeholder="Please enter your email address"
                        value={createAlertObj.alertEmail}
                        name="alertEmail"
                        onChange={onChangeHandler}
                      />
                    </label>
                  </div>
                </td>
              </tr>
              <tr>
                <th scope="col">Alert Class</th>
                <td colSpan={3}>
                  <div>
                    {/* 내가만든거 */}
                    <div className="alert-wrap">
                      {alertClassArr[`${createAlertObj.alertType}`].map((typeObj, index) => (
                        <div className="alert-type" key={`${typeObj.name}_${index}`}>
                          <div className="alert-check">
                            <input
                              type="checkbox"
                              className="ipt-check enable"
                              id={`checked-${typeObj.name}`}
                              checked={createAlertObj.alertClass.includes(typeObj.value) ? true : false}
                              name="alertClass"
                              onChange={onChangeHandler}
                              value={typeObj.value}
                            />
                            <label htmlFor={`checked-${typeObj.name}`}>
                              <span className="blind">선택</span>
                            </label>
                          </div>
                          <p className="alert-name">{typeObj.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* create버튼 */}
      <div className="account-btn">
        <button type="button" className="btn pri" onClick={onClickMoveList}>
          Cancel
        </button>
        <button type="button" className="btn pri" onClick={onClickCreateAlert}>
          Create
        </button>
      </div>
    </>
  );
};

export default CreateAlertBox;
